import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { useAuth } from "../../context/AuthContext";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { 
  Crown, 
  MessageCircle,
  Utensils,
  TrendingUp,
  ClipboardCheck,
  ChevronRight,
  Zap,
  ChefHat,
  Target,
  Sparkles,
  CheckCircle2,
  Heart,
  Brain,
  Scale,
  Camera,
  Calendar
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// WhatsApp config
const WHATSAPP_NUMBER = "34641099731";
const WHATSAPP_MESSAGE = "Hola Jordi, quiero información sobre el asesoramiento premium de GOATGYM.";

// Premium background images - Real founder photos
const IMAGES = {
  hero: "https://customer-assets.emergentagent.com/job_beginner-gains-app/artifacts/ecattype_F5717EB5-1B0D-4A1F-A1CB-23D101548C17.png",
  coaching: "https://customer-assets.emergentagent.com/job_beginner-gains-app/artifacts/6lpkvb4m_0F8F7D0A-2BAA-4BEF-9903-E8B033455BB0.jpeg",
  tracking: "https://customer-assets.emergentagent.com/job_beginner-gains-app/artifacts/5d8ko8in_00083195-1271-40DA-B5F6-3FCB66958CC5.png"
};

const PlanPage = () => {
  const navigate = useNavigate();
  const { user, getAuthHeaders } = useAuth();
  const [isPremium, setIsPremium] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkPremiumStatus();
  }, []);

  const checkPremiumStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/premium/status`, {
        headers: getAuthHeaders(),
      });
      setIsPremium(response.data.is_premium);
    } catch (error) {
      console.error("Error checking premium status:", error);
      setIsPremium(user?.is_premium || false);
    } finally {
      setLoading(false);
    }
  };

  const openWhatsApp = (message = WHATSAPP_MESSAGE) => {
    const encodedMessage = encodeURIComponent(message);
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodedMessage}`, "_blank");
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <div className="w-12 h-12 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
        <p className="text-[#666] mt-4">Cargando...</p>
      </div>
    );
  }

  // FREE user - Show Premium Landing (HIGH-TICKET CINEMATIC VERSION)
  if (!isPremium) {
    return (
      <div className="animate-fade-in -mx-4 -mt-4 overflow-x-hidden" data-testid="premium-landing-screen">
        
        {/* ============ HERO SECTION ============ */}
        <div className="relative min-h-[55vh] overflow-hidden">
          {/* Background Image */}
          <div className="absolute inset-0 z-0">
            <img 
              src={IMAGES.hero} 
              alt=""
              className="w-full h-full object-cover object-[center_15%] opacity-60"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-[#0B0B0B]/30 via-[#0B0B0B]/60 to-[#0B0B0B]" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0B0B0B]/70 to-transparent" />
            {/* Golden glow */}
            <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[500px] h-[250px] bg-[#D4AF37]/15 rounded-full blur-[100px]" />
          </div>

          {/* Hero Content */}
          <div className="relative z-10 px-5 pt-14 pb-10 text-center flex flex-col items-center justify-center min-h-[55vh]">
            {/* Premium Badge */}
            <motion.div 
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#D4AF37]/20 border border-[#D4AF37]/50 backdrop-blur-sm mb-5"
            >
              <Crown className="w-4 h-4 text-[#D4AF37]" />
              <span className="text-[#D4AF37] text-xs font-black tracking-[0.2em] uppercase">Asesoramiento Premium</span>
            </motion.div>

            {/* Title */}
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-3xl sm:text-4xl font-heading font-black text-white mb-3 leading-tight tracking-tight px-2"
              style={{ textShadow: '0 4px 30px rgba(0,0,0,0.8)' }}
            >
              Tu transformación<br />
              <span className="text-[#D4AF37]">empieza aquí</span>
            </motion.h1>

            {/* Subtitle */}
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="text-white/80 text-base max-w-xs mx-auto font-medium px-4"
              style={{ textShadow: '0 2px 15px rgba(0,0,0,0.8)' }}
            >
              Acompañamiento nutricional real, seguimiento personalizado y contacto directo.
            </motion.p>

            {/* Decorative line */}
            <motion.div
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ delay: 0.6, duration: 0.6 }}
              className="w-16 h-0.5 bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent mt-6"
            />
          </div>
        </div>

        {/* ============ TRANSFORMATION PILLARS ============ */}
        <div className="px-4 -mt-4 relative z-20">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="grid grid-cols-3 gap-3 mb-6"
          >
            {[
              { icon: Scale, label: "Nutrición", color: "from-green-500/30" },
              { icon: TrendingUp, label: "Progreso", color: "from-blue-500/30" },
              { icon: Brain, label: "Mentalidad", color: "from-purple-500/30" }
            ].map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 + (idx * 0.1) }}
                className={`bg-gradient-to-br ${item.color} to-transparent border border-white/10 p-4 text-center backdrop-blur-sm`}
              >
                <item.icon className="w-6 h-6 text-white/80 mx-auto mb-2" />
                <p className="text-white/90 text-xs font-bold">{item.label}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>

        {/* ============ WHAT'S INCLUDED ============ */}
        <div className="px-4 mb-6">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="bg-[#0F0F0F] border border-[#2A2A2A] rounded-xl p-5"
          >
            <div className="flex items-center gap-2 mb-5">
              <Sparkles className="w-5 h-5 text-[#D4AF37]" />
              <h2 className="text-white font-heading font-bold text-lg">
                ¿Qué incluye?
              </h2>
            </div>

            <div className="space-y-4">
              {[
                { icon: Utensils, title: "Plan Nutricional", desc: "Dieta adaptada a tus macros y estilo de vida", color: "text-green-400", bg: "from-green-500/20" },
                { icon: Calendar, title: "Revisiones Semanales", desc: "Ajustes continuos según tu evolución", color: "text-blue-400", bg: "from-blue-500/20" },
                { icon: Camera, title: "Check-ins de Progreso", desc: "Fotos, métricas y feedback personalizado", color: "text-purple-400", bg: "from-purple-500/20" },
                { icon: MessageCircle, title: "Contacto Directo", desc: "Acceso a tu coach por WhatsApp", color: "text-[#D4AF37]", bg: "from-[#D4AF37]/20" },
                { icon: ChefHat, title: "Recetas Premium", desc: "Ideas rápidas y deliciosas", color: "text-orange-400", bg: "from-orange-500/20" },
                { icon: Target, title: "Objetivos Claros", desc: "Metas definidas y seguimiento real", color: "text-red-400", bg: "from-red-500/20" }
              ].map((item, idx) => (
                <motion.div 
                  key={idx}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.8 + (idx * 0.08) }}
                  className="flex items-start gap-3"
                >
                  <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${item.bg} to-transparent flex items-center justify-center flex-shrink-0`}>
                    <item.icon className={`w-5 h-5 ${item.color}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-white font-bold text-sm">{item.title}</h3>
                    <p className="text-[#888] text-xs">{item.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* ============ CINEMATIC SECTION - REAL COACHING ============ */}
        <div className="relative overflow-hidden mb-6">
          <div className="absolute inset-0 z-0">
            <img 
              src={IMAGES.coaching} 
              alt=""
              className="w-full h-full object-cover object-[center_35%] opacity-30"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-[#0B0B0B] via-[#0B0B0B]/80 to-[#0B0B0B]" />
          </div>

          <div className="relative z-10 px-4 py-8">
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 1 }}
              className="text-center"
            >
              <div className="w-12 h-12 bg-[#D4AF37]/20 border border-[#D4AF37]/40 flex items-center justify-center mx-auto mb-4">
                <Heart className="w-6 h-6 text-[#D4AF37]" />
              </div>
              <h3 className="text-[#D4AF37] font-bold text-lg mb-2">Acompañamiento Real</h3>
              <p className="text-white/80 text-sm leading-relaxed max-w-xs mx-auto">
                Esto no es una app automática. Es un proceso de transformación donde trabajamos juntos para conseguir resultados de verdad.
              </p>
            </motion.div>
          </div>
        </div>

        {/* ============ TRANSFORMATION PROCESS ============ */}
        <div className="px-4 mb-6">
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.1 }}
            className="bg-gradient-to-br from-[#1A1A1A] to-[#0F0F0F] border border-[#D4AF37]/20 rounded-xl p-5"
          >
            <h3 className="text-white font-bold text-base mb-4 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-[#D4AF37]" />
              Tu proceso de cambio
            </h3>
            
            <div className="space-y-3">
              {[
                { num: "01", title: "Evaluación inicial", desc: "Analizamos tu punto de partida" },
                { num: "02", title: "Plan personalizado", desc: "Nutrición y entreno adaptados a ti" },
                { num: "03", title: "Seguimiento semanal", desc: "Revisiones y ajustes continuos" },
                { num: "04", title: "Transformación real", desc: "Resultados visibles en semanas" }
              ].map((step, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 1.2 + (idx * 0.1) }}
                  className="flex items-start gap-3"
                >
                  <div className="w-8 h-8 bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-[#D4AF37] text-xs font-black">{step.num}</span>
                  </div>
                  <div>
                    <p className="text-white text-sm font-bold">{step.title}</p>
                    <p className="text-[#666] text-xs">{step.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* ============ TRUST BADGE ============ */}
        <div className="px-4 mb-6">
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 1.3 }}
            className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-xl p-5 text-center relative overflow-hidden"
          >
            {/* Glow */}
            <div className="absolute inset-0 bg-[#D4AF37]/5 blur-xl" />
            
            <div className="relative z-10">
              <CheckCircle2 className="w-8 h-8 text-[#D4AF37] mx-auto mb-3" />
              <p className="text-[#D4AF37] font-bold text-sm mb-1">Compromiso 100% real</p>
              <p className="text-white/70 text-xs">
                Sin promesas vacías. Resultados reales con constancia.
              </p>
            </div>
          </motion.div>
        </div>

        {/* ============ FREE vs PREMIUM COMPARISON ============ */}
        <div className="px-4 mb-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.35 }}
          >
            <p className="text-[#555] text-xs uppercase tracking-[0.15em] text-center mb-4 font-bold">
              ¿Por qué pasarte a Premium?
            </p>
            
            <div className="grid grid-cols-2 gap-3">
              {/* FREE Column */}
              <div className="bg-[#1A1A1A] border border-[#2A2A2A] rounded-xl p-4">
                <div className="text-center mb-4">
                  <span className="px-3 py-1 bg-[#2A2A2A] rounded-full text-[#888] text-xs font-bold">FREE</span>
                </div>
                <div className="space-y-2.5 text-[11px]">
                  <div className="flex items-center gap-2 text-[#666]">
                    <span className="w-4 text-center">✓</span>
                    <span>Rutinas básicas</span>
                  </div>
                  <div className="flex items-center gap-2 text-[#666]">
                    <span className="w-4 text-center">✓</span>
                    <span>Ejercicios guía</span>
                  </div>
                  <div className="flex items-center gap-2 text-[#444]">
                    <span className="w-4 text-center text-red-400">✕</span>
                    <span className="line-through">Nutrición</span>
                  </div>
                  <div className="flex items-center gap-2 text-[#444]">
                    <span className="w-4 text-center text-red-400">✕</span>
                    <span className="line-through">Recetas</span>
                  </div>
                  <div className="flex items-center gap-2 text-[#444]">
                    <span className="w-4 text-center text-red-400">✕</span>
                    <span className="line-through">Seguimiento</span>
                  </div>
                  <div className="flex items-center gap-2 text-[#444]">
                    <span className="w-4 text-center text-red-400">✕</span>
                    <span className="line-through">Contacto coach</span>
                  </div>
                </div>
              </div>
              
              {/* PREMIUM Column */}
              <div className="bg-gradient-to-br from-[#D4AF37]/10 to-[#0F0F0F] border border-[#D4AF37]/40 rounded-xl p-4 relative overflow-hidden">
                {/* Glow */}
                <div className="absolute top-0 right-0 w-20 h-20 bg-[#D4AF37]/10 rounded-full blur-2xl" />
                
                <div className="text-center mb-4 relative z-10">
                  <span className="px-3 py-1 bg-[#D4AF37] rounded-full text-black text-xs font-black">PREMIUM</span>
                </div>
                <div className="space-y-2.5 text-[11px] relative z-10">
                  <div className="flex items-center gap-2 text-[#D4AF37]">
                    <span className="w-4 text-center">✓</span>
                    <span className="text-white">Rutinas avanzadas</span>
                  </div>
                  <div className="flex items-center gap-2 text-[#D4AF37]">
                    <span className="w-4 text-center">✓</span>
                    <span className="text-white">Videos ejercicios</span>
                  </div>
                  <div className="flex items-center gap-2 text-[#D4AF37]">
                    <span className="w-4 text-center">✓</span>
                    <span className="text-white font-bold">Plan nutricional</span>
                  </div>
                  <div className="flex items-center gap-2 text-[#D4AF37]">
                    <span className="w-4 text-center">✓</span>
                    <span className="text-white font-bold">+70 recetas</span>
                  </div>
                  <div className="flex items-center gap-2 text-[#D4AF37]">
                    <span className="w-4 text-center">✓</span>
                    <span className="text-white font-bold">Check-ins semanales</span>
                  </div>
                  <div className="flex items-center gap-2 text-[#D4AF37]">
                    <span className="w-4 text-center">✓</span>
                    <span className="text-white font-bold">WhatsApp directo</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* ============ CTA SECTION ============ */}
        <div className="px-4 pb-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.4 }}
          >
            <Button
              onClick={() => openWhatsApp()}
              className="w-full h-14 bg-gradient-to-r from-[#D4AF37] to-amber-500 hover:from-[#E5C54D] hover:to-amber-400 text-[#0B0B0B] font-black text-sm tracking-wider uppercase rounded-xl shadow-lg shadow-[#D4AF37]/30 transition-all active:scale-[0.98]"
              data-testid="premium-whatsapp-cta"
            >
              <MessageCircle className="w-5 h-5 mr-2 flex-shrink-0" />
              <span className="truncate">Hablar por WhatsApp</span>
            </Button>

            <p className="text-center text-[#666] text-xs mt-3">
              Escríbeme para empezar tu transformación
            </p>
          </motion.div>
        </div>

        {/* ============ FAQ ============ */}
        <div className="px-4 pb-8">
          <p className="text-[#555] text-xs uppercase tracking-[0.15em] text-center mb-4 font-bold">
            Preguntas frecuentes
          </p>
          
          <div className="space-y-2">
            {[
              { q: "¿Cómo funciona el seguimiento?", a: "Cada semana revisamos tu progreso, ajustamos la dieta y resolvemos dudas por WhatsApp." },
              { q: "¿Qué incluye el asesoramiento?", a: "Plan nutricional, revisiones periódicas, ajustes según evolución y contacto directo." },
              { q: "¿Qué resultados puedo esperar?", a: "Con constancia real, verás cambios visibles en 4-6 semanas." }
            ].map((faq, idx) => (
              <motion.div 
                key={idx}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.5 + (idx * 0.1) }}
                className="bg-[#121212] border border-[#2A2A2A] rounded-lg p-3"
              >
                <p className="text-white text-xs font-bold mb-1">{faq.q}</p>
                <p className="text-[#888] text-xs leading-relaxed">{faq.a}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // PREMIUM user - Show premium dashboard
  return (
    <div className="animate-fade-in pb-8" data-testid="premium-dashboard">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#D4AF37] to-amber-500 flex items-center justify-center">
            <Crown className="w-5 h-5 text-[#0B0B0B]" />
          </div>
          <div>
            <h1 className="text-2xl font-heading font-black text-white">
              Premium
            </h1>
            <p className="text-[#D4AF37] text-sm font-medium flex items-center gap-1">
              <Zap className="w-3 h-3" />
              Plan activo
            </p>
          </div>
        </div>
      </div>

      {/* Premium sections */}
      <div className="space-y-4">
        {/* Dieta */}
        <Card 
          className="bg-[#121212] border-[#2A2A2A] hover:border-[#D4AF37]/50 transition-all cursor-pointer active:scale-[0.99]"
          onClick={() => navigate("/plan/dieta")}
          data-testid="premium-diet-card"
        >
          <CardContent className="p-5">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-green-500/20 to-emerald-500/10 flex items-center justify-center flex-shrink-0">
                <Utensils className="w-7 h-7 text-green-400" />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-lg font-heading font-bold text-white">Dieta</h2>
                <p className="text-[#888] text-sm truncate">Tu plan de alimentación personalizado</p>
              </div>
              <ChevronRight className="w-5 h-5 text-[#555] flex-shrink-0" />
            </div>
          </CardContent>
        </Card>

        {/* Progreso */}
        <Card 
          className="bg-[#121212] border-[#2A2A2A] hover:border-[#D4AF37]/50 transition-all cursor-pointer active:scale-[0.99]"
          onClick={() => navigate("/plan/progreso")}
          data-testid="premium-progress-card"
        >
          <CardContent className="p-5">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500/20 to-cyan-500/10 flex items-center justify-center flex-shrink-0">
                <TrendingUp className="w-7 h-7 text-blue-400" />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-lg font-heading font-bold text-white">Progreso</h2>
                <p className="text-[#888] text-sm truncate">Seguimiento de peso y medidas</p>
              </div>
              <ChevronRight className="w-5 h-5 text-[#555] flex-shrink-0" />
            </div>
          </CardContent>
        </Card>

        {/* Check-in semanal */}
        <Card 
          className="bg-[#121212] border-[#2A2A2A] hover:border-[#D4AF37]/50 transition-all cursor-pointer active:scale-[0.99]"
          onClick={() => navigate("/premium/check-in")}
          data-testid="premium-checkin-card"
        >
          <CardContent className="p-5">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-500/20 to-violet-500/10 flex items-center justify-center flex-shrink-0">
                <ClipboardCheck className="w-7 h-7 text-purple-400" />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-lg font-heading font-bold text-white">Check-in</h2>
                <p className="text-[#888] text-sm truncate">Actualiza tu estado cada semana</p>
              </div>
              <ChevronRight className="w-5 h-5 text-[#555] flex-shrink-0" />
            </div>
          </CardContent>
        </Card>

        {/* Recetas */}
        <Card 
          className="bg-[#121212] border-[#2A2A2A] hover:border-[#D4AF37]/50 transition-all cursor-pointer active:scale-[0.99]"
          onClick={() => navigate("/recetas")}
          data-testid="premium-recipes-card"
        >
          <CardContent className="p-5">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-orange-500/20 to-amber-500/10 flex items-center justify-center flex-shrink-0">
                <ChefHat className="w-7 h-7 text-orange-400" />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-lg font-heading font-bold text-white">Recetas</h2>
                <p className="text-[#888] text-sm truncate">Ideas rápidas para tus comidas</p>
              </div>
              <ChevronRight className="w-5 h-5 text-[#555] flex-shrink-0" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Support banner */}
      <div className="mt-6 p-4 bg-[#1A1A1A] rounded-xl border border-[#2A2A2A]">
        <div className="flex items-center gap-3">
          <MessageCircle className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-white text-sm font-medium">¿Necesitas ayuda?</p>
            <p className="text-[#666] text-xs truncate">Contacta con tu coach</p>
          </div>
          <Button
            onClick={() => openWhatsApp("Hola Jordi, tengo una consulta sobre mi plan Premium")}
            size="sm"
            variant="outline"
            className="border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10 flex-shrink-0"
          >
            Escribir
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PlanPage;
